import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import './Benefits.css';

// Import images (reuse from loginForm)
import DriverImage from './Media/Driver.png';
import PassengerImage from './Media/Passenger.png';
import PackageImage from './Media/Package.png';

const Benefits = () => {
  const { t, i18n } = useTranslation('common');
  const currentLanguage = i18n.language || 'lv';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t('benefits.meta_title')}</title>
        <meta name="description" content={t('benefits.meta_description')} />
        <meta name="keywords" content={t('benefits.meta_keywords')} />
        <link rel="canonical" href="https://wayshare.app/benefits" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wayshare.app/benefits" />
        <meta property="og:title" content={t('benefits.meta_title')} />
        <meta property="og:description" content={t('benefits.meta_description')} />
        <meta property="og:image" content="https://wayshare.app/og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://wayshare.app/benefits" />
        <meta property="twitter:title" content={t('benefits.meta_title')} />
        <meta property="twitter:description" content={t('benefits.meta_description')} />

        {/* Language alternates for SEO */}
        <link rel="alternate" hreflang="lv" href="https://wayshare.app/lv/benefits" />
        <link rel="alternate" hreflang="en" href="https://wayshare.app/en/benefits" />
        <link rel="alternate" hreflang="ru" href="https://wayshare.app/ru/benefits" />
      </Helmet>

      <div className="benefits-page">

        {/* Hero Section */}
        <motion.section
          className="benefits-hero"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <div className="benefits-hero-content">
            <h1 className="benefits-hero-title">
              {t('benefits.hero_title')}
            </h1>
            <p className="benefits-hero-subtitle">
              {t('benefits.hero_subtitle')}
            </p>
          </div>
        </motion.section>

        {/* Main Benefits Cards */}
        <motion.section
          className="benefits-main-cards"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="container">
            <div className="benefits-grid-main">

              {/* Driver Benefits */}
              <motion.div className="benefit-main-card" variants={fadeInUp}>
                <div className="benefit-icon-wrapper">
                  <img src={DriverImage} alt={t('benefits.driver_title')} className="benefit-main-image" />
                </div>
                <h2 className="benefit-main-title">{t('benefits.driver_title')}</h2>
                <p className="benefit-main-intro">{t('benefits.driver_intro')}</p>
                <ul className="benefit-main-list">
                  <li>
                    <span className="benefit-emoji">💰</span>
                    <div>
                      <strong>{t('benefits.driver_save_money_title')}</strong>
                      <p>{t('benefits.driver_save_money_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">🌱</span>
                    <div>
                      <strong>{t('benefits.driver_environment_title')}</strong>
                      <p>{t('benefits.driver_environment_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">👥</span>
                    <div>
                      <strong>{t('benefits.driver_social_title')}</strong>
                      <p>{t('benefits.driver_social_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">🚗</span>
                    <div>
                      <strong>{t('benefits.driver_flexible_title')}</strong>
                      <p>{t('benefits.driver_flexible_desc')}</p>
                    </div>
                  </li>
                </ul>
              </motion.div>

              {/* Passenger Benefits */}
              <motion.div className="benefit-main-card" variants={fadeInUp}>
                <div className="benefit-icon-wrapper">
                  <img src={PassengerImage} alt={t('benefits.passenger_title')} className="benefit-main-image" />
                </div>
                <h2 className="benefit-main-title">{t('benefits.passenger_title')}</h2>
                <p className="benefit-main-intro">{t('benefits.passenger_intro')}</p>
                <ul className="benefit-main-list">
                  <li>
                    <span className="benefit-emoji">💵</span>
                    <div>
                      <strong>{t('benefits.passenger_affordable_title')}</strong>
                      <p>{t('benefits.passenger_affordable_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">✅</span>
                    <div>
                      <strong>{t('benefits.passenger_verified_title')}</strong>
                      <p>{t('benefits.passenger_verified_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">🛣️</span>
                    <div>
                      <strong>{t('benefits.passenger_routes_title')}</strong>
                      <p>{t('benefits.passenger_routes_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">💬</span>
                    <div>
                      <strong>{t('benefits.passenger_communication_title')}</strong>
                      <p>{t('benefits.passenger_communication_desc')}</p>
                    </div>
                  </li>
                </ul>
              </motion.div>

              {/* Package Delivery Benefits */}
              <motion.div className="benefit-main-card" variants={fadeInUp}>
                <div className="benefit-icon-wrapper">
                  <img src={PackageImage} alt={t('benefits.package_title')} className="benefit-main-image" />
                </div>
                <h2 className="benefit-main-title">{t('benefits.package_title')}</h2>
                <p className="benefit-main-intro">{t('benefits.package_intro')}</p>
                <ul className="benefit-main-list">
                  <li>
                    <span className="benefit-emoji">📦</span>
                    <div>
                      <strong>{t('benefits.package_convenient_title')}</strong>
                      <p>{t('benefits.package_convenient_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">💸</span>
                    <div>
                      <strong>{t('benefits.package_cost_title')}</strong>
                      <p>{t('benefits.package_cost_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">⚡</span>
                    <div>
                      <strong>{t('benefits.package_fast_title')}</strong>
                      <p>{t('benefits.package_fast_desc')}</p>
                    </div>
                  </li>
                  <li>
                    <span className="benefit-emoji">🔒</span>
                    <div>
                      <strong>{t('benefits.package_secure_title')}</strong>
                      <p>{t('benefits.package_secure_desc')}</p>
                    </div>
                  </li>
                </ul>
              </motion.div>

            </div>
          </div>
        </motion.section>

        {/* How Much Can You Save Section */}
        <motion.section
          className="benefits-savings"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <div className="container">
            <h2 className="section-title">{t('benefits.savings_title')}</h2>
            <p className="section-subtitle">{t('benefits.savings_subtitle')}</p>

            <div className="savings-comparison">
              <div className="savings-card savings-traditional">
                <h3>{t('benefits.savings_traditional_title')}</h3>
                <div className="savings-amount savings-high">€50</div>
                <ul className="savings-breakdown">
                  <li>{t('benefits.savings_fuel')}: €35</li>
                  <li>{t('benefits.savings_tolls')}: €10</li>
                  <li>{t('benefits.savings_parking')}: €5</li>
                </ul>
              </div>

              <div className="savings-arrow">→</div>

              <div className="savings-card savings-wayshare">
                <h3>{t('benefits.savings_wayshare_title')}</h3>
                <div className="savings-amount savings-low">€15</div>
                <p className="savings-note">{t('benefits.savings_note')}</p>
                <div className="savings-saved">
                  {t('benefits.savings_you_save')}: <strong>€35</strong>
                </div>
              </div>
            </div>

            <p className="savings-example">{t('benefits.savings_example')}</p>
          </div>
        </motion.section>

        {/* Environmental Impact Section */}
        <motion.section
          className="benefits-environment"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <div className="container">
            <h2 className="section-title">{t('benefits.environment_title')}</h2>
            <p className="section-subtitle">{t('benefits.environment_subtitle')}</p>

            <div className="environment-stats">
              <div className="environment-stat">
                <div className="stat-icon">🌍</div>
                <div className="stat-number">2.3kg</div>
                <div className="stat-label">{t('benefits.environment_co2_label')}</div>
              </div>

              <div className="environment-stat">
                <div className="stat-icon">🚗</div>
                <div className="stat-number">50%</div>
                <div className="stat-label">{t('benefits.environment_cars_label')}</div>
              </div>

              <div className="environment-stat">
                <div className="stat-icon">🌱</div>
                <div className="stat-number">1000+</div>
                <div className="stat-label">{t('benefits.environment_trees_label')}</div>
              </div>
            </div>

            <p className="environment-description">{t('benefits.environment_description')}</p>
          </div>
        </motion.section>

        {/* How It Works Section */}
        <motion.section
          className="benefits-how-it-works"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div className="container">
            <h2 className="section-title">{t('benefits.how_it_works_title')}</h2>

            <div className="steps-grid">
              <motion.div className="step-card" variants={fadeInUp}>
                <div className="step-number">1</div>
                <h3>{t('benefits.step1_title')}</h3>
                <p>{t('benefits.step1_desc')}</p>
              </motion.div>

              <motion.div className="step-card" variants={fadeInUp}>
                <div className="step-number">2</div>
                <h3>{t('benefits.step2_title')}</h3>
                <p>{t('benefits.step2_desc')}</p>
              </motion.div>

              <motion.div className="step-card" variants={fadeInUp}>
                <div className="step-number">3</div>
                <h3>{t('benefits.step3_title')}</h3>
                <p>{t('benefits.step3_desc')}</p>
              </motion.div>

              <motion.div className="step-card" variants={fadeInUp}>
                <div className="step-number">4</div>
                <h3>{t('benefits.step4_title')}</h3>
                <p>{t('benefits.step4_desc')}</p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* FAQ Section (Great for SEO) */}
        <motion.section
          className="benefits-faq"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div className="container">
            <h2 className="section-title">{t('benefits.faq_title')}</h2>

            <div className="faq-list">
              <motion.div className="faq-item" variants={fadeInUp}>
                <h3>{t('benefits.faq_q1')}</h3>
                <p>{t('benefits.faq_a1')}</p>
              </motion.div>

              <motion.div className="faq-item" variants={fadeInUp}>
                <h3>{t('benefits.faq_q2')}</h3>
                <p>{t('benefits.faq_a2')}</p>
              </motion.div>

              <motion.div className="faq-item" variants={fadeInUp}>
                <h3>{t('benefits.faq_q3')}</h3>
                <p>{t('benefits.faq_a3')}</p>
              </motion.div>

              <motion.div className="faq-item" variants={fadeInUp}>
                <h3>{t('benefits.faq_q4')}</h3>
                <p>{t('benefits.faq_a4')}</p>
              </motion.div>

              <motion.div className="faq-item" variants={fadeInUp}>
                <h3>{t('benefits.faq_q5')}</h3>
                <p>{t('benefits.faq_a5')}</p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="benefits-cta"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <div className="container">
            <div className="cta-content">
              <h2>{t('benefits.cta_title')}</h2>
              <p>{t('benefits.cta_subtitle')}</p>
              <div className="cta-buttons">
                <a href="/login" className="cta-button cta-primary">
                  {t('benefits.cta_signup')}
                </a>
                <a href="/PublishPost" className="cta-button cta-secondary">
                  {t('benefits.cta_post_ride')}
                </a>
              </div>
            </div>
          </div>
        </motion.section>

      </div>
    </>
  );
};

export default Benefits;
